import { useState } from "react";
import { Link } from "react-router-dom";
import {
  Droplets,
  Wind,
  Shield,
  PawPrint,
  Sparkles,
  Clock,
} from "lucide-react";
import { Seo, breadcrumbSchema, faqSchema, serviceSchema } from "../components/Seo";
import { PageHero } from "../components/PageHero";
import { useSiteContent } from "../lib/useSiteContent";
import { ChecklistSection } from "../components/ChecklistSection";
import { WhyChooseUs } from "../components/WhyChooseUs";
import { ProcessSteps } from "../components/ProcessSteps";
import { FaqAccordion } from "../components/FaqAccordion";
import { RelatedLinks } from "../components/RelatedLinks";
import { ServiceCTA } from "../components/ServiceCTA";

const PATH = "/carpet-cleaning-london";

const StainDisclaimer = () => {
  const [expanded, setExpanded] = useState(false);
  return (
    <div className="mt-5">
      <p className="text-slate-400 text-xs leading-relaxed">
        While we use professional-grade equipment and cleaning solutions
        designed to achieve the best possible results, we cannot guarantee
        complete removal of all stains.
        {expanded && (
          <>
            {" "}
            Some stains may be permanent or difficult to remove depending on
            factors such as the type of stain, age of the stain, carpet
            material, previous treatment attempts, and overall carpet
            condition.
          </>
        )}
      </p>
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="mt-1.5 text-xs font-semibold text-gold hover:text-gold-light transition-colors"
        data-testid="stain-disclaimer-toggle"
      >
        {expanded ? "Read less" : "Read more"}
      </button>
    </div>
  );
};

const DEFAULT_FAQS = [
  {
    question:
      "What is the best carpet cleaning method for London homes — steam or dry?",
    answer:
      "For most London homes we recommend hot-water extraction, often called steam carpet cleaning. It is the only method that flushes ground-in soil out of the carpet pile rather than just lifting the surface. Dry compound cleaning has its place — for example on certain wool blends or on shoots where carpets must be back in use immediately — but for everyday use, steam extraction restores the carpet most thoroughly.",
  },
  {
    question: "How long does carpet cleaning take to dry in London?",
    answer:
      "Most carpets are dry to the touch within 2 to 4 hours and fully dry within 6 to 8 hours. We use professional truck-mount and portable extractors that pull more moisture back out of the pile than retail machines, so drying time is significantly shorter than DIY rentals. We can also leave fans running on request.",
  },
  {
    question:
      "Will end of tenancy carpet cleaning remove pet stains and odours?",
    answer:
      "Yes, in almost all cases. We treat urine, vomit and mud stains with enzymatic pre-treatments before extraction, which breaks down the proteins causing the odour rather than just masking the smell. For very old or deeply set pet stains, we apply a sub-surface injection — and we will tell you up front if a stain looks unlikely to fully lift.",
  },
  {
    question: "How often should I have my London carpets professionally cleaned?",
    answer:
      "For a typical London household we recommend once every 12 to 18 months. Homes with pets, allergies, smokers or heavy foot traffic benefit from every 6 to 9 months. Tenants typically book a single deep extraction at the end of the tenancy to satisfy the inventory checkout report.",
  },
  {
    question:
      "Do you provide carpet cleaning to landlords and letting agents?",
    answer:
      "Yes — landlords and London letting agents make up a large portion of our carpet cleaning bookings. We are familiar with AIIC inventory expectations, work to checkout-report standards, and can issue agent-friendly invoices with the property address and tenancy reference where needed.",
  },
  {
    question:
      "Can you clean rugs, upholstery and stairs alongside the main carpets?",
    answer:
      "Yes. Stair carpets, area rugs, fabric sofas and dining chairs can be cleaned in the same visit. We pre-test colour-fast safe materials before extraction and adjust the method for delicate fabrics such as wool blends or silk blends.",
  },
];

const CHECKLIST_GROUPS = [
  {
    title: "Pre-Treatment",
    items: [
      "Visual inspection of each carpet area and stain mapping",
      "HEPA vacuum pass to remove loose soil and pet hair",
      "Spot pre-treatment with enzymatic solution for stains",
      "Targeted urine and odour neutraliser where required",
      "Edge and corner detail brush before extraction",
    ],
  },
  {
    title: "Hot-Water Extraction",
    items: [
      "Professional truck-mount or portable extractor",
      "Heated detergent solution at correct dilution",
      "Two passes — cleaning pass and rinse pass",
      "Grooming after extraction so pile sits correctly",
      "Furniture protected with foam blocks during drying",
    ],
  },
  {
    title: "Finish & Drying",
    items: [
      "Anti-static and pH-balancing rinse",
      "Optional carpet protector to repel future staining",
      "Optional deodoriser — fragrance-free available",
      "High-velocity drying fans on request",
      "Walkthrough with you to confirm every stain treated",
    ],
  },
];

const getWhyChoose = (sc) => [
  { icon: Droplets, title: sc.why1_title || "Steam carpet cleaning London", description: sc.why1_desc || "Hot-water extraction at professional pressure and temperature — the deep-clean method specified by carpet manufacturers and recommended in tenancy inventories." },
  { icon: Wind,     title: sc.why2_title || "Fast 2–4 hour drying",         description: sc.why2_desc || "Industrial-grade extractors and optional drying fans mean carpets are usable again within hours, not days." },
  { icon: Shield,   title: sc.why3_title || "Inventory & deposit safe",      description: sc.why3_desc || "Our cleaning is documented to the standard letting agents expect on a checkout report — perfect alongside an end of tenancy clean." },
  { icon: PawPrint, title: sc.why4_title || "Pet stain & odour specialists", description: sc.why4_desc || "Enzymatic pre-treatments and sub-surface injection for deeply set pet accidents — odour neutralisation, not masking." },
  { icon: Sparkles, title: sc.why5_title || "Stain treatment expertise",     description: sc.why5_desc || "Coffee, wine, ink, makeup, candle wax and curry. We carry a spotting kit on every visit and treat stains before extraction." },
  { icon: Clock,    title: sc.why6_title || "Same-day & weekend slots",      description: sc.why6_desc || "Carpet cleaning is rarely planned in advance. We routinely fit in next-day jobs and run weekend appointments year-round." },
];

const getProcess = (sc) => [
  { title: sc.step1_title || "Quote in minutes",    description: sc.step1_desc || "Tell us how many rooms or square metres of carpet, plus your London postcode. We price per room with optional add-ons like rugs and stairs." },
  { title: sc.step2_title || "Inspect & pre-treat", description: sc.step2_desc || "On arrival we vacuum, identify stains, apply enzyme pre-treatments and prepare the room for extraction." },
  { title: sc.step3_title || "Steam extract",       description: sc.step3_desc || "Hot-water extraction with professional equipment — typically a two-pass clean to fully flush the pile." },
  { title: sc.step4_title || "Groom & finish",      description: sc.step4_desc || "We groom the pile, place foam blocks under any furniture being replaced, and walk through with you before we leave." },
];

const CarpetCleaningLondon = () => {
  const sc = useSiteContent("carpet", {
    eyebrow: "Carpet Cleaning London", h1: "Professional Steam Carpet Cleaning", highlight: "Across London",
    subheading: "Hot-water extraction that lifts ground-in dirt, pet odours, allergens and stains — fast-drying, inventory-safe and gentle enough for wool carpets.",
    overview_eyebrow: "Service Overview", overview_heading: "Steam carpet cleaning London tenants and landlords actually trust",
    overview_p1: "A London carpet sees more wear than almost any other surface in the home. Shoe traffic from pavements and the Tube, urban soot drifting through windows, pet hair, food crumbs and the inevitable wine spill mean even brand-new carpets look tired inside twelve months. A professional steam carpet cleaning visit pulls all of that buildup out of the pile in a single appointment and resets the carpet to the closest possible state to its original colour and texture.",
    overview_p2: "Our method is hot-water extraction — the technique manufacturers recommend and the only carpet cleaning method accepted by most inventory clerks for an end of tenancy carpet cleaning sign-off. Hot, detergent-infused water is injected into the carpet at pressure, then immediately extracted along with the dissolved soil. The fibres are left clean rather than coated, and drying time is typically two to four hours.",
    overview_p3: "Whether you are a tenant booking a final clean to recover your deposit, a landlord refreshing a flat between tenancies, or a homeowner tackling years of foot traffic, the booking process is the same — and we coordinate with your wider clean.",
    checklist_eyebrow: "What's Included", checklist_heading: "The three stages of a professional carpet clean", checklist_sub: "A proper steam clean is not just about the machine. The pre-treatment, the extraction technique and the finish all influence the final result.",
    why_eyebrow: "Why Book Us", why_heading: "Why London chooses Prime Tenancy for carpets", why_sub: "A carpet clean should outlast the visit by months. The detail we apply pre-extraction is what makes the result stick.",
    process_eyebrow: "The Process", process_heading: "From booking to back in your living room — four steps",
    spec_eyebrow: "End of tenancy carpet cleaning", spec_heading: "The carpet clean inventory clerks look for at checkout",
    spec_p1: "\"Professionally cleaned\" is the wording most London tenancy agreements use, and it has a specific meaning: hot-water extraction by a third-party contractor, with a receipt. A vacuum and a stain spot-treatment will not satisfy a checkout report. We issue a dated invoice on company letterhead that you can hand straight to your letting agent as evidence of the clean.",
    spec_p2: "If you book end of tenancy carpet cleaning alongside the full inventory clean, our team coordinates the visit so the carpets are completed last and dry by the time the inventory clerk arrives.",
    spec_sidebar: "Stains we tackle every week",
    faq_heading: "Carpet cleaning London FAQs", faq_sub: "The questions tenants and landlords ask before they book.",
    faq1_q: DEFAULT_FAQS[0].question, faq1_a: DEFAULT_FAQS[0].answer,
    faq2_q: DEFAULT_FAQS[1].question, faq2_a: DEFAULT_FAQS[1].answer,
    faq3_q: DEFAULT_FAQS[2].question, faq3_a: DEFAULT_FAQS[2].answer,
    faq4_q: DEFAULT_FAQS[3].question, faq4_a: DEFAULT_FAQS[3].answer,
    faq5_q: DEFAULT_FAQS[4].question, faq5_a: DEFAULT_FAQS[4].answer,
    faq6_q: DEFAULT_FAQS[5].question, faq6_a: DEFAULT_FAQS[5].answer,
    cta_title: "Book carpet cleaning in London", cta_desc: "Fixed per-room pricing, fast 2-4 hour drying and weekend slots. Get your quote now.",
  });
  const WHY_CHOOSE = getWhyChoose(sc);
  const PROCESS    = getProcess(sc);
  const FAQS = [
    { question: sc.faq1_q, answer: sc.faq1_a }, { question: sc.faq2_q, answer: sc.faq2_a },
    { question: sc.faq3_q, answer: sc.faq3_a }, { question: sc.faq4_q, answer: sc.faq4_a },
    { question: sc.faq5_q, answer: sc.faq5_a }, { question: sc.faq6_q, answer: sc.faq6_a },
  ];
  return (
    <>
      <Seo
        title="Carpet Cleaning London — Professional Steam Carpet Cleaners"
        description="Professional carpet cleaning in London. Hot-water extraction, fast 2-4 hour drying, end of tenancy carpet cleaning and pet-stain specialists across all 32 boroughs."
        ogTitle="Carpet Cleaning London | Steam Carpet Cleaning Experts"
        ogDescription="Inventory-safe carpet cleaning by an insured London team. Hot-water extraction, fast drying, weekend slots."
        path={PATH}
        schema={[
          breadcrumbSchema([
            { name: "Services", path: PATH },
            { name: "Carpet Cleaning London", path: PATH },
          ]),
          faqSchema(FAQS),
          serviceSchema({
            name: "Carpet Cleaning London",
            serviceType: "Carpet Cleaning",
            description:
              "Hot-water extraction carpet cleaning across London — end of tenancy carpet cleaning, pet-stain removal, fast drying.",
          }),
        ]}
      />

      <PageHero
        eyebrow={sc.eyebrow}
        h1={sc.h1}
        highlight={sc.highlight}
        subheading={sc.subheading}
        breadcrumbs={[
          { name: "Services", path: PATH },
          { name: "Carpet Cleaning London", path: PATH },
        ]}
        image="https://images.unsplash.com/photo-1558317374-067fb5f30001?w=1920&q=80"
      />

      <section className="py-20 md:py-28 bg-white">
        <div className="container mx-auto px-6 md:px-12 lg:px-20">
          <div className="max-w-3xl">
            <span className="text-teal font-semibold text-sm uppercase tracking-wider">{sc.overview_eyebrow}</span>
            <h2 className="font-heading text-3xl md:text-4xl font-semibold text-navy mt-3 mb-6 leading-tight">{sc.overview_heading}</h2>
            <div className="text-slate-700 text-base md:text-lg leading-relaxed space-y-5">
              <p>{sc.overview_p1}</p>
              <p>{sc.overview_p2}</p>
              <p>{sc.overview_p3}{" "}Pair carpet cleaning with our specialist{" "}
                <Link to="/end-of-tenancy-cleaning-london" className="text-teal font-medium hover:text-navy">end of tenancy cleaning in London</Link>{" "}for a single contractor finish.
              </p>
            </div>
          </div>
        </div>
      </section>

      <ChecklistSection eyebrow={sc.checklist_eyebrow} heading={sc.checklist_heading} subheading={sc.checklist_sub} groups={CHECKLIST_GROUPS} background="slate" />
      <WhyChooseUs eyebrow={sc.why_eyebrow} heading={sc.why_heading} subheading={sc.why_sub} items={WHY_CHOOSE} />
      <ProcessSteps eyebrow={sc.process_eyebrow} heading={sc.process_heading} steps={PROCESS} background="slate" />

      <section className="py-20 md:py-28 bg-white">
        <div className="container mx-auto px-6 md:px-12 lg:px-20">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 max-w-6xl mx-auto items-start">
            <div>
              <span className="text-teal font-semibold text-sm uppercase tracking-wider">{sc.spec_eyebrow}</span>
              <h2 className="font-heading text-3xl md:text-4xl font-semibold text-navy mt-3 mb-5 leading-tight">{sc.spec_heading}</h2>
              <p className="text-slate-700 leading-relaxed mb-4">{sc.spec_p1}</p>
              <p className="text-slate-700 leading-relaxed">
                {sc.spec_p2}{" "}See full{" "}
                <Link to="/prices" className="text-teal font-medium hover:text-navy">carpet cleaning prices</Link>{" "}or{" "}
                <Link to="/contact" className="text-teal font-medium hover:text-navy">book a quote</Link>.
              </p>
            </div>

            <div className="bg-navy rounded-2xl p-8 md:p-10 text-white">
              <h3 className="font-heading text-2xl font-semibold mb-5">{sc.spec_sidebar}</h3>
              <ul className="grid grid-cols-2 gap-3">
                {[
                  "Red wine",
                  "Coffee & tea",
                  "Pet urine",
                  "Curry & turmeric",
                  "Mud & soil",
                  "Candle wax",
                  "Makeup & foundation",
                  "Ink & marker",
                  "Vomit",
                  "Tube grime",
                ].map((item) => (
                  <li
                    key={item}
                    className="flex items-center gap-2 text-slate-200 text-sm"
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-gold flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
              <StainDisclaimer />
            </div>
          </div>
        </div>
      </section>

      <FaqAccordion heading={sc.faq_heading} subheading={sc.faq_sub} faqs={FAQS} background="slate" />
      <RelatedLinks currentPath={PATH} />
      <ServiceCTA title={sc.cta_title} description={sc.cta_desc} variant="navy" />
    </>
  );
};

export default CarpetCleaningLondon;
